import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2, Copy, ExternalLink, Loader2, Flame, Wallet, Info, ShieldCheck, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import Reveal, { Eyebrow, EASE } from "@/components/Reveal";
import Roadmap from "@/components/Roadmap";
import Tokenomics from "@/components/Tokenomics";
import { useWallet } from "@/lib/wallet";
import WalletButton from "@/components/WalletButton";
import { payPresale, toBaseUnits, toLamports, BW_MINT } from "@/lib/pay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const short = (w) => `${w.slice(0, 4)}…${w.slice(-4)}`;
const fmtDay = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function usePresale() {
  const [stats, setStats] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const refresh = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        fetch(`${API}/presale/stats`).then((r) => r.json()),
        fetch(`${API}/presale/purchases`).then((r) => r.json()),
      ]);
      setStats(s);
      setPurchases(p);
    } catch (e) {}
  }, []);
  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 8000);
    return () => clearInterval(iv);
  }, [refresh]);
  return { stats, purchases, refresh };
}

function BuyCard({ stats, refresh }) {
  const { wallet, address } = useWallet();
  const [currency, setCurrency] = useState("SOL");
  const [amount, setAmount] = useState("0.5");
  const [buying, setBuying] = useState(false);
  const [purchase, setPurchase] = useState(null);

  const rate = stats?.sol_rate;
  const amt = parseFloat(amount) || 0;
  const usdValue = currency === "SOL" ? (rate ? amt * rate : null) : amt;
  const tokenEstimate = usdValue ? Math.floor((usdValue / stats.price) * 100) / 100 : null;
  const soldOut = stats && stats.remaining <= 0;

  const toastedRef = useRef(false);
  const deliveredRef = useRef(false);

  useEffect(() => {
    if (!purchase) return;
    if (purchase.status === "failed") return;
    if (purchase.status === "confirmed" && purchase.delivery_status === "sent") return;
    const iv = setInterval(async () => {
      try {
        const r = await fetch(`${API}/presale/purchase/${purchase.id}`);
        const d = await r.json();
        if (d.status === "confirmed" && !toastedRef.current) {
          toastedRef.current = true;
          toast.success("Purchase confirmed on Solana");
          refresh();
        }
        if (d.delivery_status === "sent" && !deliveredRef.current) {
          deliveredRef.current = true;
          toast.success(`${d.tokens.toLocaleString()} B&W delivered to your wallet`);
          refresh();
        }
        setPurchase((prev) => (prev && prev.status === d.status && prev.delivery_status === d.delivery_status ? prev : d));
      } catch (e) {}
    }, 4000);
    return () => clearInterval(iv);
  }, [purchase?.id, purchase?.status, purchase?.delivery_status, refresh]);

  const units = (() => {
    try {
      return currency === "SOL" ? toLamports(amount) : toBaseUnits(amount);
    } catch (e) {
      return null;
    }
  })();

  const buy = async () => {
    if (!wallet?.provider || !address) return;
    if (!units) {
      toast.error(currency === "SOL" ? "Enter a SOL amount (max 9 decimals)" : "Enter a USDC amount (max 6 decimals)");
      return;
    }
    if (soldOut) {
      toast.error("Presale allocation sold out");
      return;
    }
    if (tokenEstimate !== null && stats && tokenEstimate > stats.remaining) {
      toast.error(`Only ${stats.remaining.toLocaleString()} B&W remain in the presale`);
      return;
    }
    setBuying(true);
    try {
      const signature = await payPresale({ provider: wallet.provider, payer: address, currency, amountUnits: units });
      const r = await fetch(`${API}/presale/buy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, currency, amount, signature }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not record purchase");
      setPurchase(d);
      toast.success("Transaction submitted", { description: "Confirming on Solana…" });
    } catch (e) {
      toast.error(e?.message?.slice(0, 140) || "Purchase failed");
    }
    setBuying(false);
  };

  const [rechecking, setRechecking] = useState(false);
  const recheck = async () => {
    if (!purchase?.id || rechecking) return;
    setRechecking(true);
    try {
      const r = await fetch(`${API}/presale/purchase/${purchase.id}`);
      const d = await r.json();
      if (r.ok) {
        setPurchase(d);
        if (d.status !== "failed") toast.success("Found on-chain — updating");
        else toast.info("Still not on-chain. If you just paid, wait a minute and check again.");
      }
    } catch (e) {}
    setRechecking(false);
  };

  const inputCls =
    "w-full rounded-lg border border-line bg-panel px-4 py-3 font-mono text-lg text-white outline-none transition-colors focus:border-sgreen/60";

  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-panel" data-testid="presale-buy-card">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-line bg-card2/60 px-5 py-3.5">
        <span className="font-mono text-xs text-mut">
          b&w presale · {stats?.active_round ? `${stats.active_round.name.toLowerCase()} · mainnet` : "mainnet"}
        </span>
        <span className="flex items-center gap-2 whitespace-nowrap font-mono text-[10px] uppercase tracking-widest text-sgreen">
          <span className="ping-dot h-1.5 w-1.5 rounded-full bg-sgreen" />
          {stats ? `${stats.remaining.toLocaleString()} left` : "loading"}
          {stats && <span className="hidden text-mut sm:inline">/ {stats.supply.toLocaleString()}</span>}
        </span>
      </div>

      {purchase && purchase.status === "confirmed" ? (
        <div className="p-6 text-center sm:p-8" data-testid="presale-success">
          <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full border border-sgreen/40 bg-sgreen/10">
            {purchase.delivery_status === "sent" ? (
              <CheckCircle2 className="h-8 w-8 text-sgreen" />
            ) : (
              <Loader2 className="h-8 w-8 animate-spin text-sgreen" />
            )}
          </div>
          <h3 className="mt-5 text-2xl font-semibold text-white">
            {purchase.delivery_status === "sent" ? "Tokens delivered." : "You're in."}
          </h3>
          <p className="mt-2 font-mono text-sm text-sgreen">
            {purchase.delivery_status === "sent"
              ? `${purchase.tokens.toLocaleString()} B&W sent to ${short(purchase.wallet)}`
              : `${purchase.tokens.toLocaleString()} B&W reserved for ${short(purchase.wallet)} — delivering automatically…`}
          </p>
          {purchase.delivery_status === "sent" ? (
            <a
              href={`https://solscan.io/tx/${purchase.delivery_signature}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="presale-delivery-link"
              className="mt-5 inline-flex items-center gap-1.5 font-mono text-xs text-sgreen"
            >
              View token delivery on Solscan <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <p className="mt-3 font-mono text-[11px] leading-relaxed text-mut" data-testid="presale-delivery-note">
              Payment confirmed on-chain. Your B&W transfers to your wallet automatically
              {purchase.delivery_note ? ` — ${purchase.delivery_note}` : " — usually within a minute"}.
            </p>
          )}
          <p className="mt-2 text-xs text-mut">
            Paid {purchase.amount.toFixed(4)} {purchase.currency} (~${purchase.usd_value.toFixed(2)}).
          </p>
          <a
            href={`https://solscan.io/tx/${purchase.signature}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="presale-explorer-link"
            className="mt-3 inline-flex items-center gap-1.5 font-mono text-xs text-mut transition-colors hover:text-sgreen"
          >
            View payment on Solscan <ExternalLink className="h-3 w-3" />
          </a>
          <div className="mt-6">
            <button
              onClick={() => {
                toastedRef.current = false;
                deliveredRef.current = false;
                setPurchase(null);
              }}
              data-testid="presale-buy-again-button"
              className="rounded-full border border-line px-6 py-3 text-sm text-white transition-colors hover:border-white/30"
            >
              Buy more
            </button>
          </div>
        </div>
      ) : stats && !stats.open ? (
        <div className="p-6 text-center sm:p-8" data-testid="presale-closed">
          <h3 className="text-2xl font-semibold text-white">The presale has ended.</h3>
          <p className="mt-2 font-mono text-sm text-mut">
            Unsold tokens were burned. $B&W lists on a Solana DEX{stats?.tge_date ? ` — TGE ${fmtDay(stats.tge_date)}` : ""}.
          </p>
        </div>
      ) : (
        <div className="p-6 sm:p-8" data-testid="presale-buy-form">
          <div className="flex gap-2">
            {[
              ["SOL", "Pay with SOL"],
              ["USDC", "Pay with USDC"],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => {
                  setCurrency(id);
                  setAmount(id === "SOL" ? "0.5" : "50");
                }}
                data-testid={`presale-currency-${id.toLowerCase()}`}
                className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                  currency === id ? "border-sgreen/50 bg-sgreen/10 text-sgreen" : "border-line bg-card2 text-mut hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mut" htmlFor="presale-amount-input">
              Amount ({currency})
            </label>
            <input
              id="presale-amount-input"
              data-testid="presale-amount-input"
              type="number"
              min="0"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className={inputCls}
              placeholder={currency === "SOL" ? "0.5" : "50"}
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {(currency === "SOL" ? ["0.25", "0.5", "1", "2"] : ["25", "50", "100", "500"]).map((q) => (
                <button
                  key={q}
                  onClick={() => setAmount(q)}
                  data-testid={`presale-quick-${q}`}
                  className="rounded-full border border-line bg-card2 px-3.5 py-1.5 font-mono text-xs text-mut transition-colors hover:border-sgreen/40 hover:text-white"
                >
                  {q} {currency}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-sgreen/25 bg-sgreen/[0.05] p-5 text-center" data-testid="presale-quote">
            {currency === "SOL" && !rate ? (
              <p className="font-mono text-sm text-mut">SOL price feed unavailable — pay with USDC instead.</p>
            ) : tokenEstimate ? (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mut">You get</p>
                <p className="mt-2 font-mono text-4xl font-bold text-sgreen" data-testid="presale-quote-tokens">
                  {tokenEstimate.toLocaleString("en-US", { maximumFractionDigits: 2 })}
                  <span className="ml-2 text-base font-medium text-white">B&W</span>
                </p>
                <p className="mt-2 font-mono text-[11px] text-mut">
                  ≈ ${usdValue.toFixed(2)} · worth ${tokenEstimate && (tokenEstimate * stats.listing_price).toLocaleString("en-US", { maximumFractionDigits: 2 })} at listing
                </p>
              </>
            ) : (
              <p className="font-mono text-sm text-mut">Enter an amount above.</p>
            )}
          </div>

          {stats && stats.active_round && (
            <p className="mt-3 text-center font-mono text-[11px] text-mut" data-testid="presale-limits-note">
              ${stats.min_buy_usd.toFixed(0)} minimum · up to ${stats.active_round.max_wallet_usd.toLocaleString()} per wallet in {stats.active_round.name} · ${stats.active_round.price.toFixed(2)} per B&W
            </p>
          )}

          {purchase && purchase.status === "submitted" ? (
            <div className="mt-6 flex items-center justify-center gap-3 rounded-xl border border-sgreen/30 bg-sgreen/[0.06] py-4" data-testid="presale-confirming">
              <Loader2 className="h-4 w-4 animate-spin text-sgreen" />
              <span className="font-mono text-sm text-sgreen">Confirming on Solana…</span>
            </div>
          ) : address ? (
            <button
              onClick={buy}
              disabled={buying || !units || (currency === "SOL" && !rate)}
              data-testid="presale-buy-button"
              className="cta-glow mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sgreen px-6 py-4 text-base font-semibold text-black disabled:opacity-40"
            >
              {buying ? <Loader2 className="h-5 w-5 animate-spin" /> : <Flame className="h-5 w-5" />}
              {buying ? "Approve in wallet…" : `Buy ${tokenEstimate ? tokenEstimate.toLocaleString("en-US", { maximumFractionDigits: tokenEstimate < 1000 ? 2 : 0 }) : ""} B&W`}
            </button>
          ) : (
            <div className="mt-6 rounded-xl border border-line bg-card2 p-5 text-center" data-testid="presale-connect-gate">
              <p className="text-sm text-mut">Connect a Solana wallet to buy B&W with SOL or USDC.</p>
              <div className="mt-4">
                <WalletButton variant="solid" />
              </div>
            </div>
          )}
          {purchase && purchase.status === "failed" && (
            <div className="mt-4 rounded-xl border border-red-400/25 bg-red-400/[0.05] p-4" data-testid="presale-failed-note">
              <p className="text-center font-mono text-xs leading-relaxed text-red-400">
                {purchase.fail_reason || "That transaction didn't land on-chain — you can buy again safely."}
              </p>
              <div className="mt-3 flex items-center justify-center gap-5">
                <button
                  onClick={recheck}
                  disabled={rechecking}
                  data-testid="presale-recheck-button"
                  className="inline-flex items-center gap-1.5 font-mono text-xs text-white transition-colors hover:text-sgreen disabled:opacity-50"
                >
                  {rechecking ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                  Check again
                </button>
                {purchase.signature && (
                  <a
                    href={`https://solscan.io/tx/${purchase.signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="presale-failed-explorer-link"
                    className="inline-flex items-center gap-1.5 font-mono text-xs text-mut transition-colors hover:text-sgreen"
                  >
                    View on Solscan <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Presale() {
  const { stats, purchases, refresh } = usePresale();
  const scrollRef = useRef(null);
  const progress = stats ? Math.min(100, (stats.sold_tokens / stats.supply) * 100) : 0;

  return (
    <main className="relative min-h-screen overflow-hidden pt-[72px]" data-testid="presale-page">
      <div className="bg-grid absolute inset-0" />
      <div className="animate-glow pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-sgreen/15 blur-[140px]" />

      <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-12">
        <Reveal className="text-center">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-sgreen/40 bg-sgreen/10 px-4 py-1.5" data-testid="presale-badge">
            <Flame className="h-3.5 w-3.5 text-sgreen" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-sgreen">
              {stats?.active_round ? `${stats.active_round.name} live · ${stats.rounds.length} rounds` : "Presale · limited supply"}
            </span>
          </div>
          <h1 className="mx-auto mt-7 max-w-3xl text-4xl font-bold tracking-[-0.02em] sm:text-5xl lg:text-6xl">
            Buy $B&W from <span className="text-sgreen">$0.20.</span>
            <br />
            Listing at $0.45.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-mut sm:text-lg">
            Three rounds, 250,000 $BNW, $70,000 target. Pay with SOL or USDC — every payment moves
            on-chain, straight to the presale wallet. Tokens unlock 25% at launch, the rest over 3 months.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-12 rounded-2xl border border-line bg-card2 p-6 sm:p-7" data-testid="presale-progress-card">
            <div className="flex flex-wrap items-center justify-between gap-3 font-mono text-sm">
              <p className="text-white">
                Raised <span className="text-sgreen">${(stats?.usd_raised ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}</span>
                <span className="text-mut"> of $70,000 target</span>
              </p>
              <p className="text-mut">
                {stats ? `${stats.sold_tokens.toLocaleString()} / ${stats.supply.toLocaleString()} B&W sold` : "—"}
              </p>
            </div>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-white/[0.06]">
              <motion.div
                data-testid="presale-progress-bar"
                className="h-full rounded-full bg-sgreen"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, ease: EASE }}
              />
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ["Round price", stats ? (stats.price ? `$${stats.price.toFixed(2)}` : "—") : "—", "presale-stat-price"],
                ["Listing ref", stats ? `$${stats.listing_price}` : "—", "presale-stat-listing"],
                ["Remaining", stats ? stats.remaining.toLocaleString() : "—", "presale-stat-remaining"],
                ["Buyers", stats ? String(stats.buyers) : "—", "presale-stat-buyers"],
              ].map(([k, v, tid]) => (
                <div key={k} className="rounded-xl border border-line bg-panel p-4 text-center" data-testid={tid}>
                  <p className="font-mono text-xl font-semibold text-white">{v}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">{k}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.06}>
          <div className="mt-12" data-testid="presale-rounds">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-2xl font-bold tracking-[-0.02em] text-white sm:text-3xl">Presale rounds</h2>
              <p className="font-mono text-xs text-mut">unsold tokens roll into the next round · anything left after Round 3 is burned</p>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {(stats?.rounds || []).map((r, i) => (
                <div
                  key={r.id}
                  data-testid={`presale-round-${i}`}
                  className={`rounded-2xl border p-6 ${r.status === "active" ? "border-sgreen/40 bg-sgreen/[0.05]" : "border-line bg-card2"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-lg font-semibold text-white">{r.name}</h3>
                    <span
                      className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${
                        r.status === "active" ? "bg-sgreen text-black" : r.status === "closed" ? "bg-white/[0.06] text-mut" : "border border-line bg-panel text-mut"
                      }`}
                    >
                      {r.status === "active" && stats?.price && stats.price !== r.price ? "early" : r.status}
                    </span>
                  </div>
                  <p className="mt-4 font-mono text-3xl font-bold text-sgreen">
                    ${r.price.toFixed(2)}
                    <span className="text-sm font-medium text-mut"> / B&W</span>
                  </p>
                  <div className="mt-5 space-y-1.5 font-mono text-xs">
                    <p className="flex justify-between"><span className="text-mut">Window</span><span className="text-white">{fmtDay(r.starts_at)} – {fmtDay(r.ends_at)}</span></p>
                    <p className="flex justify-between"><span className="text-mut">Allocation</span><span className="text-white">{r.tokens.toLocaleString()} $BNW</span></p>
                    <p className="flex justify-between"><span className="text-mut">Max raise</span><span className="text-white">${r.max_raise_usd.toLocaleString()}</span></p>
                    <p className="flex justify-between"><span className="text-mut">Per wallet</span><span className="text-white">up to ${r.max_wallet_usd.toLocaleString()}</span></p>
                  </div>
                  <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                    <div
                      className={`h-full rounded-full ${r.status === "active" ? "bg-sgreen" : "bg-white/25"}`}
                      style={{ width: `${Math.min(100, (r.raised_usd / r.max_raise_usd) * 100)}%` }}
                    />
                  </div>
                  <p className="mt-2 font-mono text-[10px] text-mut">${r.raised_usd.toLocaleString("en-US", { maximumFractionDigits: 2 })} raised</p>
                </div>
              ))}
            </div>
            <p className="mt-4 rounded-xl border border-line bg-card2 px-5 py-3.5 text-center font-mono text-[11px] leading-relaxed text-mut" data-testid="presale-early-note">
              Buying is open now — early purchases settle at the next round's price and count toward its per-wallet cap.
            </p>
          </div>
        </Reveal>

        <div className="mt-8 grid min-w-0 gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal delay={0.1} className="min-w-0">
            <BuyCard stats={stats} refresh={refresh} />
          </Reveal>

          <Reveal delay={0.16} className="min-w-0">
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl border border-line bg-card2 p-6 sm:p-7" data-testid="presale-token-card">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
                  <Info className="h-4 w-4 text-sgreen" />
                  Token details
                </h3>
                <div className="mt-5 space-y-1">
                  {[
                    ["Name", "$B&W"],
                    ["Round price", stats ? (stats.price ? `$${stats.price.toFixed(2)}` : "—") : "—"],
                    ["Listing ref", stats ? `$${stats.listing_price}` : "—"],
                    ["Presale allocation", stats ? stats.supply.toLocaleString() : "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between border-b border-line/70 py-2.5 font-mono text-sm">
                      <span className="text-mut">{k}</span>
                      <span className="text-white">{v}</span>
                    </div>
                  ))}
                  <div className="py-3">
                    <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mut">Mint address</p>
                    <button
                      onClick={() => navigator.clipboard?.writeText(stats?.mint || BW_MINT).then(() => toast.success("Mint address copied"), () => {})}
                      data-testid="presale-mint-copy"
                      className="group flex w-full items-center gap-2.5 rounded-lg border border-line bg-panel px-4 py-3 text-left transition-colors hover:border-sgreen/40"
                    >
                      <span className="min-w-0 flex-1 truncate font-mono text-xs text-white/80">{BW_MINT || "—"}</span>
                      <Copy className="ml-auto h-4 w-4 shrink-0 text-mut group-hover:text-white" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-line bg-card2 p-6 sm:p-7" data-testid="presale-recent">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
                  <TrendingUp className="h-4 w-4 text-sgreen" />
                  Latest buys
                </h3>
                {purchases.length === 0 ? (
                  <p className="mt-5 rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-mut" data-testid="presale-recent-empty">
                    No purchases yet — be the first name on this board.
                  </p>
                ) : (
                  <div className="mt-5 space-y-2" data-testid="presale-recent-list">
                    {purchases.map((p, i) => (
                      <div key={i} className="flex items-center justify-between rounded-xl border border-line bg-panel px-4 py-3" data-testid={`presale-recent-row-${i}`}>
                        <span className="min-w-0 flex-1 truncate font-mono text-xs text-white" title={p.wallet}>
                          {short(p.wallet)}
                          {p.tokens >= 2000 && <span className="ml-2 rounded-full bg-sgreen/10 px-2 py-0.5 text-[10px] text-sgreen">whale</span>}
                        </span>
                        <span className="shrink-0 font-mono text-[11px] text-sgreen">
                          +{p.tokens.toLocaleString()} B&W
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="overflow-hidden rounded-2xl border border-line bg-card2 p-6" data-testid="presale-safety">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
                  <ShieldCheck className="h-4 w-4 text-sgreen" />
                  How it works
                </h3>
                <ol className="mt-4 space-y-3 text-sm leading-relaxed text-mut">
                  <li>1. Connect your Solana wallet — Phantom or Solflare.</li>
                  <li>2. Choose SOL or USDC and enter an amount — $25 minimum.</li>
                  <li>3. Approve the transfer — funds move on-chain, directly to the presale wallet. We never hold keys.</li>
                  <li>4. Your B&W is delivered to your wallet automatically right after the payment confirms.</li>
                  <li>5. Tokens unlock 25% at TGE (Nov 24, 2026), the remaining 75% linearly over 3 months.</li>
                  <li>6. Soft cap $10,000 — an 80% refund can be requested within 6 days of purchase.</li>
                </ol>
              </div>
            </div>
          </Reveal>
        </div>

        <Tokenomics />
        <Roadmap />
      </div>
    </main>
  );
}
